/**
 * EventsDebugSection — the Debug menu's "Events & battles" panel: every LIVE
 * staged event (feast, festival, rite, hustings, speech, street scene), every
 * battle (marching / engaged / just resolved) and every legacy war clash,
 * each with a TELEPORT button that moves the admin to it (debugTeleport →
 * adopt, the world-map pattern). Refreshes every 2 s from the rows the
 * living-events modules already subscribe to — no extra subscriptions.
 * Registered by `EventsDebugModule` (main.ts, right after the Debug menu —
 * `getModule('DebugMenu')` is undefined before it exists).
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { EVENT_KINDS, PLAN_LABEL, type BattlePlan } from '@shared/living_events.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { settlementNamesFor } from '../../lib/settlement_names.js';
import { civilizationOf } from '../../lib/biome_settlements.js';
import type { AtlasDebugMenuModule } from '../AtlasDebugMenuModule.js';

interface Row { key: string; glyph: string; label: string; detail: string; x: number; z: number; sortKey: number }

let names: Map<number, string> | null = null;
let namesSeed = '';

function settlementName(settlementId: bigint): string {
  const conn = atlas.conn;
  if (!conn) return `#${settlementId}`;
  let seed = '';
  for (const w of conn.db.world.iter()) if (w.kind === 'commons') { seed = w.terrainSeed; break; }
  if (!seed) return `#${settlementId}`;
  if (!names || namesSeed !== seed) { names = settlementNamesFor(settlementSites(seed) as SettlementSite[], civilizationOf); namesSeed = seed; }
  return names.get(Number(settlementId) - 1) ?? `#${settlementId}`; // DB settlementId = site.id + 1
}

const fmtLeft = (endsUs: bigint): string => {
  const s = Math.max(0, Number(endsUs - BigInt(Date.now()) * 1000n) / 1e6);
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};
const dist = (x: number, z: number): string => {
  const d = Math.hypot(x - atlas.localX, z - atlas.localZ);
  return d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`;
};

/** Everything an admin can jump to right now. */
export function eventRows(): { events: Row[]; battles: Row[] } {
  const conn = atlas.conn;
  const events: Row[] = [], battles: Row[] = [];
  if (!conn) return { events, battles };
  const nowUs = BigInt(Date.now()) * 1000n;
  try {
    for (const e of conn.db.livingEvent.iter()) {
      if (e.phase !== 'live' || e.endsAt.microsSinceUnixEpoch <= nowUs || e.family === 'battle') continue;
      const def = EVENT_KINDS[e.kind];
      events.push({
        key: `e${e.eventId}`, glyph: def?.glyph ?? '•', label: def?.label ?? e.kind,
        detail: `${e.title} · ${settlementName(e.settlementId)} · ${dist(e.x, e.z)} · ${fmtLeft(e.endsAt.microsSinceUnixEpoch)} left`,
        x: e.x, z: e.z, sortKey: Math.hypot(e.x - atlas.localX, e.z - atlas.localZ),
      });
    }
  } catch { /* pre-publish server */ }
  try {
    for (const b of conn.db.battle.iter()) {
      const phase = b.phase === 'march' ? 'columns marching' : b.phase === 'engaged' ? `round ${b.round} — ${PLAN_LABEL[b.planA as BattlePlan] ?? b.planA} vs ${PLAN_LABEL[b.planB as BattlePlan] ?? b.planB}` : `resolved: ${b.winner === 'a' ? 'attackers won' : b.winner === 'b' ? 'defenders held' : 'no victor'}`;
      battles.push({
        key: `b${b.battleId}`, glyph: '⚔', label: b.kind === 'siege' ? 'Siege' : 'Battle',
        detail: `${settlementName(b.aggressorSid)} → ${settlementName(b.defenderSid)} · ${phase} · ${dist(b.targetX, b.targetZ)}`,
        x: b.targetX, z: b.targetZ, sortKey: (b.phase === 'resolved' ? 1e9 : 0) + Math.hypot(b.targetX - atlas.localX, b.targetZ - atlas.localZ),
      });
    }
  } catch { /* pre-publish server */ }
  try {
    const wc = (conn.db as unknown as { warClash?: { iter(): Iterable<{ clashId: bigint; x: number; z: number; aggressorSid: bigint; defenderSid: bigint; fallenName: string; endsAt: { microsSinceUnixEpoch: bigint } }> } }).warClash;
    for (const c of wc?.iter() ?? []) {
      if (c.endsAt.microsSinceUnixEpoch <= nowUs) continue;
      battles.push({
        key: `c${c.clashId}`, glyph: '⚔', label: 'Skirmish',
        detail: `${settlementName(c.aggressorSid)} vs ${settlementName(c.defenderSid)}${c.fallenName ? ` · ${c.fallenName} fell` : ''} · ${dist(c.x, c.z)} · ${fmtLeft(c.endsAt.microsSinceUnixEpoch)} left`,
        x: c.x, z: c.z, sortKey: Math.hypot(c.x - atlas.localX, c.z - atlas.localZ),
      });
    }
  } catch { /* pre-publish server */ }
  events.sort((a, b) => a.sortKey - b.sortKey);
  battles.sort((a, b) => a.sortKey - b.sortKey);
  return { events, battles };
}

/** Move the admin next to a point: the server moves the row, then the client ADOPTS it (the world-map pattern). */
export async function teleportTo(x: number, z: number, what: string): Promise<boolean> {
  const conn = atlas.conn;
  if (!conn) { bus.emit('atlas:status', { text: 'not connected' }); return false; }
  const tx = x + 5, tz = z + 5; // beside the venue, not on the bonfire
  try {
    await conn.reducers.debugTeleport({ posX: tx, posZ: tz });
  } catch (e) {
    bus.emit('atlas:status', { text: `teleport refused: ${(e as Error).message}` });
    return false;
  }
  // Wait for our own player_state row to land (≤ 10 s), then adopt.
  for (let i = 0; i < 40; i++) {
    const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    if (me && Math.hypot(me.posX - tx, me.posZ - tz) < 2) break;
    await new Promise((r) => setTimeout(r, 250));
  }
  if (atlas.currentWorldId !== null) bus.emit('atlas:world-adopted', { worldId: atlas.currentWorldId, x: tx, z: tz });
  bus.emit('atlas:status', { text: `Teleported to ${what}` });
  return true;
}

/** Register the section on the Debug menu (idempotent). */
export function registerEventsDebugSection(menu: AtlasDebugMenuModule): void {
  menu.registerSection('Events & battles', (body) => {
    body.id = 'debug-events-section';
    const note = document.createElement('div');
    note.style.cssText = 'color:#9aa;margin-bottom:4px';
    note.textContent = 'live staged events + battles — click to teleport';
    // "Start war here": the two nearest settled towns declare a day-long war
    // whose first battle musters AT the admin's feet (debugStartWarAt).
    const warBtn = document.createElement('button');
    warBtn.id = 'debug-start-war-here';
    warBtn.textContent = '⚔ Start war at my position';
    warBtn.style.cssText = 'display:block;margin:0 0 6px;padding:3px 10px;font:12px system-ui;background:#4a2733;color:#fdd;border:1px solid #765;border-radius:4px;cursor:pointer';
    const startWarHere = async (durationS = 0): Promise<boolean> => {
      const conn = atlas.conn;
      if (!conn) { bus.emit('atlas:status', { text: 'not connected' }); return false; }
      try {
        await conn.reducers.debugStartWarAt({ x: atlas.localX, z: atlas.localZ, durationS });
        bus.emit('atlas:status', { text: '⚔ War declared — the columns muster at your feet' });
        return true;
      } catch (e) {
        bus.emit('atlas:status', { text: `war refused: ${(e as Error).message}` });
        return false;
      }
    };
    warBtn.onclick = () => { warBtn.disabled = true; void startWarHere(0).finally(() => { warBtn.disabled = false; }); };
    // "Start a battle here" (war tweaks 2026-09-17): the nearest city/town's DECLARED war opens a fresh field battle
    // at the admin's feet (debugStartBattleAt) — the second button once a war is set.
    const battleBtn = document.createElement('button');
    battleBtn.id = 'debug-start-battle-here';
    battleBtn.textContent = '⚔ Start a battle at my position';
    battleBtn.style.cssText = 'display:block;margin:0 0 6px;padding:3px 10px;font:12px system-ui;background:#4a3a27;color:#fed;border:1px solid #765;border-radius:4px;cursor:pointer';
    const startBattleHere = async (): Promise<boolean> => {
      const conn = atlas.conn;
      if (!conn) { bus.emit('atlas:status', { text: 'not connected' }); return false; }
      try {
        await conn.reducers.debugStartBattleAt({ x: atlas.localX, z: atlas.localZ });
        bus.emit('atlas:status', { text: '⚔ Battle opened — both columns muster at your feet' });
        return true;
      } catch (e) {
        bus.emit('atlas:status', { text: `battle refused: ${(e as Error).message}` });
        return false;
      }
    };
    battleBtn.onclick = () => { battleBtn.disabled = true; void startBattleHere().finally(() => { battleBtn.disabled = false; }); };
    const list = document.createElement('div');
    list.id = 'debug-events-list';
    body.append(note, warBtn, battleBtn, list);
    const btnCss = 'margin:2px 0 2px 6px;padding:1px 7px;font:11px system-ui;background:#27334a;color:#dde;border:1px solid #456;border-radius:4px;cursor:pointer';
    let lastSig = '';
    const render = (): void => {
      const { events, battles } = eventRows();
      const sig = [...events, ...battles].map((r) => `${r.key}|${r.detail}`).join('\n');
      if (sig === lastSig) return;
      lastSig = sig;
      list.replaceChildren();
      const group = (title: string, rows: Row[], empty: string): void => {
        const h = document.createElement('div');
        h.textContent = title;
        h.style.cssText = 'color:#cdf;margin:4px 0 2px;font-weight:600';
        list.appendChild(h);
        if (rows.length === 0) { const d = document.createElement('div'); d.textContent = empty; d.style.color = '#778'; list.appendChild(d); return; }
        for (const r of rows.slice(0, 12)) {
          const row = document.createElement('div');
          row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:4px;margin:1px 0';
          row.dataset.key = r.key;
          const text = document.createElement('span');
          text.textContent = `${r.glyph} ${r.label} — ${r.detail}`;
          text.style.cssText = 'flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
          text.title = `${r.label}: ${r.detail}`;
          const btn = document.createElement('button');
          btn.textContent = 'teleport';
          btn.className = 'debug-events-tp';
          btn.style.cssText = btnCss;
          btn.onclick = () => { btn.disabled = true; void teleportTo(r.x, r.z, `${r.label} (${r.detail.split(' · ')[0]})`).finally(() => { btn.disabled = false; }); };
          row.append(text, btn);
          list.appendChild(row);
        }
        if (rows.length > 12) { const d = document.createElement('div'); d.textContent = `… ${rows.length - 12} more (nearest first)`; d.style.color = '#778'; list.appendChild(d); }
      };
      group('Events', events, 'no live events right now');
      group('Battles & clashes', battles, 'no battle under way');
    };
    render();
    window.setInterval(() => { if (body.offsetParent !== null) render(); }, 2000); // 24 DOM rows + 3 table scans only while open
    (window as unknown as Record<string, unknown>).__eventsDebug = {
      rows: () => eventRows(),
      teleport: (key: string) => { const r = [...eventRows().events, ...eventRows().battles].find((x) => x.key === key); return r ? teleportTo(r.x, r.z, r.label) : Promise.resolve(false); },
      startWarHere: (durationS = 0) => startWarHere(durationS),
      startBattleHere: () => startBattleHere(),
    };
  });
}

/** Registers the section once the Debug menu exists (main.ts orders it after AtlasDebugMenuModule). */
export class EventsDebugModule implements IModule {
  readonly name = 'EventsDebug';
  init(app: App): void {
    const menu = app.getModule<AtlasDebugMenuModule>('DebugMenu');
    if (menu) registerEventsDebugSection(menu);
  }
}
